/**
 * CrmActivities - Página do Feed Global de Atividades do CRM
 * Centraliza todas as atividades do tenant em uma única timeline
 * 
 * Layout: Timeline 2/3 + Card KPI unificado (Balance Card) + Calendário 1/3
 * 
 * @version 2.0.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial do feed de atividades
 * RELEASE: 2026-02-09 v1.1.0 - Novo layout com timeline 2/3 e KPIs 1/3 verticais
 * RELEASE: 2026-02-09 v1.2.0 - Card KPI unificado estilo Balance Card
 * RELEASE: 2026-02-09 v1.3.0 - Abrir modal de negócio ao clicar na referência
 * RELEASE: 2026-02-09 v1.4.0 - Sistema de abas para filtrar por tipo de atividade
 * RELEASE: 2026-02-09 v1.5.0 - Badges de contadores pendentes nas abas
 * RELEASE: 2026-02-09 v1.6.0 - Integração com RPC de KPIs e filtro de período no Balance Card
 * RELEASE: 2026-02-09 v2.0.0 - Novos filtros: Período, Workspace, Vendedor, Responsável, Status
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, X, CheckCheck } from 'lucide-react';
import { startOfMonth, endOfMonth, subDays, startOfDay, endOfDay, subMonths } from 'date-fns';
import { Button } from '@/components/ui/button';
import { ActivitiesFeed } from '@/components/crm/activities/ActivitiesFeed';
import { ActivitiesFilters, type ActivityFilters } from '@/components/crm/activities/ActivitiesFilters';
import { ActivitiesTabs, type ActivityFeedTab } from '@/components/crm/activities/ActivitiesTabs';
import { ActivitiesMiniCalendar } from '@/components/crm/activities/ActivitiesMiniCalendar';
import { ActivitiesBalanceCard } from '@/components/crm/activities/ActivitiesBalanceCard';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { useActivitiesFeed } from '@/hooks/useActivitiesFeed';
import { useActivitiesFeedCounts } from '@/hooks/useActivitiesFeedCounts';
import { useActivitiesBalanceKpi } from '@/hooks/useActivitiesBalanceKpi';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import type { ActivityType } from '@/hooks/useCrmDealActivities';
import type { PeriodPreset, PeriodFilter } from '@/hooks/useCrmOverviewFilters';

/**
 * Calcula as datas de início e fim baseado no preset selecionado
 */
function calculatePeriodDates(preset: PeriodPreset): { startDate: Date; endDate: Date } {
  const today = new Date();
  
  switch (preset) {
    case 'today':
      return { startDate: startOfDay(today), endDate: endOfDay(today) };
    case 'yesterday':
      const yesterday = subDays(today, 1);
      return { startDate: startOfDay(yesterday), endDate: endOfDay(yesterday) };
    case '7days':
      return { startDate: startOfDay(subDays(today, 6)), endDate: endOfDay(today) };
    case '15days':
      return { startDate: startOfDay(subDays(today, 14)), endDate: endOfDay(today) };
    case 'month':
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
    case 'lastMonth':
      const lastMonth = subMonths(today, 1);
      return { startDate: startOfMonth(lastMonth), endDate: endOfMonth(lastMonth) };
    default:
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
  }
}

/**
 * Página principal do Feed de Atividades
 */
export default function CrmActivities() {
  const navigate = useNavigate();
  
  // Dados do perfil do usuário atual
  const { profile } = useAuth();

  // Estado da aba ativa
  const [activeTab, setActiveTab] = useState<ActivityFeedTab>('all');

  // Estado inicial dos filtros consolidados
  const initialPeriod: PeriodFilter = useMemo(() => {
    const today = new Date();
    return {
      preset: 'month' as PeriodPreset,
      startDate: startOfMonth(today),
      endDate: endOfMonth(today),
    };
  }, []);

  // Estado dos filtros consolidado (v2.0.0)
  const [filters, setFilters] = useState<ActivityFilters>({
    period: initialPeriod,
    workspaceId: null,
    sellerId: null,
    performedByUserId: null,
    status: 'all',
    origin: 'all',
    activityType: undefined,
    onlyWithComments: false,
    selectedDate: undefined,
  });

  // Verifica se algum filtro difere do padrão
  const hasActiveFilters =
    filters.period.preset !== 'month' ||
    filters.workspaceId !== null ||
    filters.sellerId !== null ||
    filters.performedByUserId !== null ||
    filters.status !== 'all' ||
    (filters.origin ?? 'all') !== 'all';

  // Handler para limpar todos os filtros
  const handleClearFilters = () => {
    const today = new Date();
    setFilters({
      period: {
        preset: 'month' as PeriodPreset,
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
      },
      workspaceId: null,
      sellerId: null,
      performedByUserId: null,
      status: 'all',
      origin: 'all',
      activityType: filters.activityType,
      onlyWithComments: filters.onlyWithComments,
      selectedDate: undefined,
    });
  };

  // Handler para mudança de filtros com recalculo de datas de período
  const handleFiltersChange = (newFilters: ActivityFilters) => {
    // Se o preset mudou (não é custom), recalcula as datas
    if (newFilters.period.preset !== 'custom' && newFilters.period.preset !== filters.period.preset) {
      const dates = calculatePeriodDates(newFilters.period.preset);
      setFilters({
        ...newFilters,
        period: {
          preset: newFilters.period.preset,
          startDate: dates.startDate,
          endDate: dates.endDate,
        },
      });
    } else {
      setFilters(newFilters);
    }
  };

  // Hook para buscar KPIs do Balance Card
  const { kpiData, isLoading: isLoadingKpi } = useActivitiesBalanceKpi({
    startDate: filters.period.startDate,
    endDate: filters.period.endDate,
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
    status: filters.status,
  });
  
  // Estado do modal de negócio
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [isDealModalOpen, setIsDealModalOpen] = useState(false);

  // Abre modal de detalhes do negócio
  const handleOpenDeal = (dealId: string) => {
    setSelectedDealId(dealId);
    setIsDealModalOpen(true);
  };

  // Handler para mudança de aba
  const handleTabChange = (tab: ActivityFeedTab) => {
    setActiveTab(tab);

    if (tab === 'comments') {
      // Aba de comentários: limpa tipo, ativa filtro server-side de menções
      setFilters(prev => ({
        ...prev,
        activityType: undefined,
        origin: 'all',
        onlyWithComments: false,
        onlyWithMyMentions: true,
      }));
    } else if (tab === 'system') {
      // Aba Sistema: lista apenas atividades automáticas (is_system=true)
      setFilters(prev => ({
        ...prev,
        activityType: undefined,
        origin: 'system',
        onlyWithComments: false,
        onlyWithMyMentions: false,
      }));
    } else if (tab === 'all') {
      // Aba "Todos": limpa tipo, origem e filtros de comentário/menção
      setFilters(prev => ({
        ...prev,
        activityType: undefined,
        origin: 'all',
        onlyWithComments: false,
        onlyWithMyMentions: false,
      }));
    } else {
      // Abas de tipo específico (manuais)
      setFilters(prev => ({
        ...prev,
        activityType: tab as ActivityType,
        origin: 'all',
        onlyWithComments: false,
        onlyWithMyMentions: false,
      }));
    }
  };

  // Hook para buscar atividades com filtros (convertendo para formato esperado pelo hook)
  const activitiesFeedFilters = useMemo(() => ({
    activityType: filters.activityType,
    onlyWithComments: filters.onlyWithComments,
    onlyWithMyMentions: filters.onlyWithMyMentions,
    selectedDate: filters.selectedDate,
    // Novos filtros v2.0.0
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
    status: filters.status,
    // v1.7.2: Filtro de origem
    origin: filters.origin,
    startDate: filters.period.startDate,
    endDate: filters.period.endDate,
  }), [filters]);

  const { 
    activities, 
    isLoading, 
    refreshAll, 
    isRefetching,
    completeActivity,
    reopenActivity,
    deleteActivity,
    // v1.11.0: Marca "Lido" para Notas/Sistema
    markActivityRead,
    markActivityUnread,
    markAllRead,
    // v2.0.0: Scroll infinito
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useActivitiesFeed({ filters: activitiesFeedFilters });

  // Hook para buscar contadores por tipo de atividade (v1.3.0: inclui período)
  const { counts, isLoading: isLoadingCounts } = useActivitiesFeedCounts({
    workspaceId: filters.workspaceId,
    sellerId: filters.sellerId,
    performedByUserId: filters.performedByUserId,
    status: filters.status,
    startDate: filters.period.startDate,
    endDate: filters.period.endDate,
  });

  return (
    <div className="space-y-4">
      {/* Cabeçalho: Título + Refresh | Filtros (scroll) */}
      <div className="flex items-center gap-2">
        {/* Esquerda: Título + Refresh (não encolhe) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <h1 className="text-lg md:text-2xl font-bold tracking-tight">Feed Atividades</h1>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => refreshAll()}
            disabled={isRefetching}
            title="Atualizar dados"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isRefetching && "animate-spin")} />
          </Button>
        </div>

        {/* Separador visual */}
        <span className="text-muted-foreground/30 shrink-0">|</span>

        {/* Centro: Filtros com scroll horizontal */}
        <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none min-w-0 flex-1">
          <ActivitiesFilters filters={filters} onFiltersChange={handleFiltersChange} />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="h-[22px] px-1.5 text-xs text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Abas de tipo de atividade com contadores + ação "Marcar todas como lidas" */}
      <div className="mb-4 flex items-end justify-between gap-3">
        <div className="flex-1 min-w-0">
          <ActivitiesTabs 
            activeTab={activeTab} 
            onTabChange={handleTabChange}
            counts={{
              all: counts.all,
              note: counts.note,
              call: counts.call,
              email: counts.email,
              meeting: counts.meeting,
              task: counts.task,
              system: counts.system,
              comments: counts.comments,
            }}
            isLoadingCounts={isLoadingCounts}
          />
        </div>

        {/* v1.11.0: Botão "Marcar todas como lidas" (apenas Notas/Sistema) — inline com as abas */}
        {(activeTab === 'note' || activeTab === 'system') && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-accent shrink-0 mb-1"
            disabled={markAllRead.isPending}
            onClick={() =>
              markAllRead.mutate({
                scope: activeTab as 'note' | 'system',
                workspaceId: filters.workspaceId,
                sellerId: filters.sellerId,
                performedByUserId: filters.performedByUserId,
                startDate: filters.period.startDate,
                endDate: filters.period.endDate,
              })
            }
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/* Grid: Timeline 3/4 + KPIs 1/4 vertical */}
      <div className="grid gap-4 md:grid-cols-4" style={{ minHeight: '100vh' }}>
        {/* Timeline - altura calculada para alinhar com sidebar (scroll interno) */}
        <div 
          className="md:col-span-3 bg-card rounded-lg border p-4 overflow-hidden"
          style={{ height: '100vh' }}
        >
          <ActivitiesFeed
            activities={activities}
            isLoading={isLoading}
            onComplete={(id) => completeActivity.mutate(id)}
            onReopen={(id) => reopenActivity.mutate(id)}
            onDelete={(id) => deleteActivity.mutate(id)}
            onMarkRead={(id) => markActivityRead.mutate(id)}
            onMarkUnread={(id) => markActivityUnread.mutate(id)}
            isCompleting={completeActivity.isPending}
            isReopening={reopenActivity.isPending}
            isDeleting={deleteActivity.isPending}
            isMarkingRead={markActivityRead.isPending || markActivityUnread.isPending}
            onOpenDeal={handleOpenDeal}
            commentsDefaultExpanded={activeTab === 'comments'}
            hasNextPage={hasNextPage}
            fetchNextPage={() => fetchNextPage()}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        {/* Coluna KPIs vertical - 1/4 (sticky para acompanhar scroll) */}
        <div className="flex flex-col gap-4 md:sticky md:top-4 self-start">
          {/* Card KPI Premium (Balance Card) com dados da RPC */}
          <ActivitiesBalanceCard
            totalCount={kpiData.totalActivities}
            pendingCount={kpiData.pendingCount}
            completedCount={kpiData.completedCount}
            completionPercentage={kpiData.completionPercentage}
            pendingComparison={kpiData.pendingComparison}
            completedComparison={kpiData.completedComparison}
            isLoading={isLoadingKpi}
            avatarUrl={profile?.avatar_url}
            userName={profile?.full_name}
            periodFilter={filters.period}
            onPresetChange={(preset) => {
              const dates = calculatePeriodDates(preset);
              setFilters(prev => ({
                ...prev,
                period: { preset, startDate: dates.startDate, endDate: dates.endDate },
              }));
            }}
            onCustomChange={(startDate, endDate) => {
              setFilters(prev => ({
                ...prev,
                period: { preset: 'custom', startDate, endDate },
              }));
            }}
          />

          {/* Mini-Calendário */}
          <ActivitiesMiniCalendar 
            onSelectDate={(date) => setFilters(prev => ({ ...prev, selectedDate: date }))}
            selectedDate={filters.selectedDate}
          />
        </div>
      </div>

      {/* Modal de detalhes do negócio */}
      <DealDetailsModalPremium
        dealId={selectedDealId}
        open={isDealModalOpen}
        onOpenChange={(open) => {
          setIsDealModalOpen(open);
          if (!open) setSelectedDealId(null);
        }}
      />
    </div>
  );
}
